import * as React from 'react';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import { connect } from 'react-redux';

import InfoNote from 'components/info-note/InfoNote';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';
import { getItem, setItem } from 'utils/storage/localStorage';

import './import-task-status-banners.scss';

applyTranslations(require('./import-task-status-banners.i18n.json'));

import { State as GlobalStateAlias, ListImportTask } from 'types';

interface OwnProps {
  importTasks: Array<ListImportTask>,
  onAllBannersHidden: (v: boolean) => void
}

interface StateProps {
  currentOrganizationId: number | null
}

type Props = OwnProps & StateProps;

interface State {
  hiddenTaskIds: Array<number>
}

type SeenTask = {
  id: number,
  seenAt: number,
  status: ListImportTask['status']
}

class ListImportTaskStatusBanners extends React.Component<Props, State> {
  private MAX_STORAGE_SIZE = 1024 * 100; // 100 Kb
  private HOW_MANY_TO_CUT = 10;
  private TEXTS = {
    DONE: __t('import_task_status_banner.done'),
    IN_PROGRESS: __t('import_task_status_banner.progress'),
    INFLUENCERS_DATA_GATHERING: __t('import_task_status_banner.gathering'),
    NOT_IMPORTED: __t('import_task_status_banner.not_imported')
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      hiddenTaskIds: this.getHiddenTaskIds(
        props.currentOrganizationId,
        props.importTasks
      )
    };
  }

  componentDidUpdate(prevProps: Props) {
    const {
      currentOrganizationId,
      importTasks,
      onAllBannersHidden
    } = this.props;
    const { hiddenTaskIds } = this.state;

    if (
      prevProps.importTasks.length !== importTasks.length
      || prevProps.currentOrganizationId !== currentOrganizationId
    ) {
      const newHiddenTaskIds = this.getHiddenTaskIds(
        currentOrganizationId,
        importTasks
      );

      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ hiddenTaskIds: newHiddenTaskIds }, () => {
        if (importTasks.length !== newHiddenTaskIds.length) {
          onAllBannersHidden(false);
        }
      });

      return;
    }

    if (currentOrganizationId === null) {
      return;
    }

    importTasks.forEach(
      task => this.taskHasBeenSeen(currentOrganizationId, task.taskId, task.status)
    );

    if (importTasks.length === hiddenTaskIds.length) {
      onAllBannersHidden(true);
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    const { importTasks, currentOrganizationId } = this.props;
    const { hiddenTaskIds } = this.state;

    return !isEqual(importTasks, nextProps.importTasks)
      || currentOrganizationId !== nextProps.currentOrganizationId
      || !isEqual(hiddenTaskIds, nextState.hiddenTaskIds);
  }

  private getHiddenTaskIds(orgId: number | null, tasks: Array<ListImportTask>): Array<number> {
    if (orgId === null) {
      return [];
    }

    return this.squeezeTasks(tasks).map(task => {
      const hidden = !this.shouldShowBanner(
        orgId,
        task.taskId,
        task.status
      );

      return hidden ? task.taskId : -1;
    }).filter(id => id !== -1);
  }

  private squeezeTasks(tasks: Array<ListImportTask>): Array<ListImportTask> {
    const TASK_STATUS_RANKS = {
      IN_PROGRESS: 0,
      INFLUENCERS_DATA_GATHERING: 1,
      DONE: 2
    };
    const tasksDict = tasks.reduce(
      (acc, val) => {
        if (isNil(acc[val.taskId])) {
          acc[val.taskId] = val;

          return acc;
        }

        const dictRank = TASK_STATUS_RANKS[acc[val.taskId].status];
        const valRank = TASK_STATUS_RANKS[val.status];

        if (dictRank < valRank) {
          acc[val.taskId] = val;
        }

        return acc;
      },
      {} as { [k: number]: ListImportTask }
    );

    return Object
      .keys(tasksDict)
      .map(id => tasksDict[id]);
  }

  private isFilteringRequired(tasks: Array<SeenTask>): boolean {
    try {
      return new Blob([JSON.stringify(tasks)]).size > this.MAX_STORAGE_SIZE;
    } catch (error) {
      return false;
    }
  }

  private filterOldTasks(tasks: Array<SeenTask>): Array<SeenTask> {
    const copy = [...tasks];

    copy.sort((a, b) => a.seenAt - b.seenAt);

    return copy.slice(0, copy.length - this.HOW_MANY_TO_CUT);
  }

  private getStorageKey(orgId: number): string {
    return `organization-id/${orgId}/list-import-tasks-that-had-been-already-seen`;
  }

  private getSeenTasks(key: string): Array<SeenTask> {
    try {
      const seenTasksRaw = getItem(key);

      return JSON.parse(seenTasksRaw || '[]') ?? [];
    } catch (error) {
      logError(error);

      return [];
    }
  }

  private shouldShowBanner(
    orgId: number,
    taskId: number,
    taskStatus: ListImportTask['status']
  ): boolean {
    const key = this.getStorageKey(orgId);
    const seenTasks = this.getSeenTasks(key);
    const task = seenTasks.find(t => t.id === taskId && t.status === taskStatus);

    return task === undefined;
  }

  private taskHasBeenSeen(
    orgId: number,
    taskId: number,
    taskStatus: ListImportTask['status']
  ): void {
    const key = this.getStorageKey(orgId);
    let seenTasks = this.getSeenTasks(key);
    const seenTask = seenTasks.find(t => t.id === taskId && t.status === taskStatus);

    if (seenTask === undefined) {
      seenTasks.push({ id: taskId, seenAt: Date.now(), status: taskStatus });
    }

    if (this.isFilteringRequired(seenTasks)) {
      seenTasks = this.filterOldTasks(seenTasks);
    }

    try {
      setItem(key, JSON.stringify(seenTasks));
    } catch (error) {
      logError(error);
    }
  }

  private hideBannerForTaskId(taskId: number): void {
    const { importTasks, onAllBannersHidden } = this.props;
    const { hiddenTaskIds } = this.state;

    this.setState({ hiddenTaskIds: [...hiddenTaskIds, taskId] }, () => {
      if (importTasks.length === hiddenTaskIds.length) {
        return;
      }

      onAllBannersHidden(true);
    });
  }

  renderTask = (task: ListImportTask, key: number): JSX.Element => {
    const mainMessage = this.TEXTS[task.status];

    return (
      <div
        key={key}
        className="import-task-status-banner__info-note-wrapper"
      >
        <InfoNote
          hideIcon
          type="info"
        >
          <div className="import-task-status-banner__container">
            <div className="import-task-status-banner__text-container">
              <span className="import-task-status-banner__main-message">
                {mainMessage}
              </span>
              {task.errors.length ? (
                <div className="import-task-status-banner__errors-container">
                  <span
                    className="import-task-status-banner__error"
                  >
                    {this.TEXTS.NOT_IMPORTED}
                  </span>
                  {task.errors.map((err, index) => (
                    <span
                      key={index}
                      className="import-task-status-banner__url"
                    >
                      {err.url}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="import-task-status-banner__icon-container">
              <SVGDynamicIcon
                name="cross-custom-fg"
                customColor="grey"
                height={8}
                width={8}
                onClick={() => this.hideBannerForTaskId(task.taskId)}
              />
            </div>
          </div>
        </InfoNote>
      </div>
    );
  };

  render(): Array<JSX.Element> {
    const { importTasks } = this.props;
    const { hiddenTaskIds } = this.state;

    return this.squeezeTasks(importTasks)
      .filter(t => !hiddenTaskIds.includes(t.taskId))
      .map(this.renderTask);
  }
}

const mapStateToProps = ({ organizations }: GlobalStateAlias): StateProps => ({
  currentOrganizationId: organizations.currentOrganizationId
});

export default connect(mapStateToProps)(ListImportTaskStatusBanners);
